import React, {Fragment, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';
import cs from 'classnames';
import every from 'lodash/every';
import {useSelector} from 'react-redux';
import {withRouter, WithRouterProps} from 'react-router';

import {ACCEPTANCE_RTK_TAGS, useAcceptance} from 'app/acceptance/api';
import {PendingAcceptanceInfoBar} from 'app/acceptance/components/PendingAcceptanceInfoBar';
import {COMMENT_TAG} from 'app/comments/api';
import {GenericDocumentActionButtons} from 'app/components/general/GenericDocumentActionButtons';
import VersionInfoBar from 'app/components/general/VersionInfoBar';
import {VersionDiffer} from 'app/components/VersionDiffer';
import {useCurrentUserState} from 'app/current-user/api';
import {ViewStepsModeTitle} from 'app/general/detailpage/ViewStepsModeTitle';
import {showNoApprovedVersionsAlertBoxIfNotApproved} from 'app/general/structures/document-alerts/NoApprovedVersionsAlertBox';
import {SPPage} from 'app/general/structures/SPPage';
import {LANE_RTK_TAGS, useLanesForDocument} from 'app/lanes/api';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {toggleProcedureAdvancedMode} from 'app/procedures/actions';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {ProcedureStep} from 'app/procedures/components/ProcedureDetailPage/ProcedureStep';
import {
  ProcedureDiagramMobile,
  ProcedureDiagramSidebar,
  ToggleProcedureDiagramMobileButton,
} from 'app/procedures/components/ProcedureDiagram';
import {ProcedureHeaderOptionsDropdown} from 'app/procedures/components/ProcedureHeaderOptionsDropdown';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {ViewableChoices} from 'app/procedures/components/ViewableChoices';
import {removeOrphanChoices} from 'app/procedures/utils';
import {setProcessSideDiagramState} from 'app/process/actions';
import {
  PROCESS_RTK_TAGS,
  useProcessByHashid,
  useProcessVersionDetail,
  useProcessVersions,
} from 'app/process/api';
import EmptyProcessList from 'app/process/components/EmptyProcessList';
import {ProcessPageSidebar} from 'app/process/components/ProcessPageSidebar';
import {ViewProcessMetadata} from 'app/process/components/ViewProcessMetadata';
import {processOrVersionToContent} from 'app/process/content-converters';
import {getMemoizedProcessOrphanSteps} from 'app/process/selectors';
import {formatPreviousProcessVersion} from 'app/process/utils';
import {ReviewReminderWarning} from 'app/review/components/ReviewReminderWarning';
import {useAccountSettingsState} from 'app/settings/api';
import {SignoffForm} from 'app/signoffs/components/SignoffForm';
import {SignoffRequiredInfoBar} from 'app/signoffs/components/SignoffRequiredInfoBar';
import {StepFooter} from 'app/steps/components/StepFooter';
import {sweetAPI} from 'app/store/sweet-api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {getProcessName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';
import {getComparedVersion} from 'app/utils/showdiff';
import {isLastVersion} from 'app/version/utils';

import type {ProcessVersionSerializedType} from 'sweet-types';

interface Props
  extends WithRouterProps<{
    hashid: string;
    versionid: null | string;
  }> {
  route?: {
    props: {
      showAccept: string;
    };
  };
}

export function BaseProcessPage({location, route, params, router}: Props) {
  const {versionid: versionId, hashid: hashId} = params;
  const {data: process, refetch: fetchProcess} = useProcessByHashid(hashId);
  const {data: processVersion} = useProcessVersionDetail(
    versionId == null ? skipToken : {versionId}
  );
  const {data: processVersions, refetch: fetchProcessVersions} =
    useProcessVersions({processId: process?.id}, {skip: process == null});
  const {refetch: refetchLanes} = useLanesForDocument(
    process == null
      ? skipToken
      : {
          object_ids: process.id,
          content_type: process.content_type,
        }
  );

  const canDisplayVersionInterface =
    versionId != null && processVersion != null;

  const acceptance = useAcceptance(
    process == null
      ? skipToken
      : {
          object_pk: process?.id,
          content_type: process?.content_type,
        }
  ).data?.results?.[0];

  const [showOverlayDiagram, setShowOverlayDiagram] = useState(false);

  const user = useCurrentUserState().data!;
  const settings = useAccountSettingsState().data!;

  const deletingDocument = useSelector(
    (state) => state.toasts.isDeletingDocument
  );
  const closedSideDiagram = useSelector((state) =>
    state.processArea.getIn(
      ['closedSideDiagrams', process?.id.toString()],
      !user.is_diagrams_expanded
    )
  );
  const advancedMode = useSelector((state) =>
    state.proceduresPage.get('procedureAdvancedMode')
  );

  const actions = useAsyncActions({
    setProcessSideDiagramState,
    toggleProcedureAdvancedMode,
    invalidateTags: sweetAPI.util.invalidateTags,
  });

  const loadProcess = whenVisible(() => {
    return Promise.all([
      fetchProcess(),
      fetchProcessVersions(),
      refetchLanes(),
    ]);
  });

  useModelChangeSubscription(
    (message, {runWhenAny, modelIdMatch, allRelatedMatch}) => {
      if (deletingDocument) {
        return;
      }

      if (process != null) {
        const expectedRelated = {
          content_type: 'process',
          id: process.id,
        };

        runWhenAny(
          message,
          [
            modelIdMatch('process', process.id),
            allRelatedMatch('comment', expectedRelated),
            allRelatedMatch('processversion', expectedRelated),
            allRelatedMatch('review', expectedRelated),
          ],
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: LANE_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCESS_RTK_TAGS.HASHID, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: PROCESS_RTK_TAGS.TAGS, id: 'list'},
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'},
            ]);
            return loadProcess();
          }
        );
        runWhenAny(message, [allRelatedMatch('comment', expectedRelated)], () =>
          // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
          actions.invalidateTags([{type: COMMENT_TAG, id: 'list'}])
        );
      }

      if (canDisplayVersionInterface) {
        const expectedRelatedVersion = {
          content_type: 'processversion',
          id: processVersion.id,
        };
        runWhenAny(
          message,
          [allRelatedMatch('acceptance', expectedRelatedVersion)],
          () => {
            actions.invalidateTags([
              // @ts-expect-error we inject tags on the API after it's created so TS doesn't know any tag type
              {type: ACCEPTANCE_RTK_TAGS.TAGS, id: 'list'},
            ]);
          }
        );
      }
    },
    [deletingDocument, process, processVersion]
  );

  if (process == null || processVersions == null) {
    return null;
  }

  const advancedModeEnabled = versionId == null ? advancedMode : false;
  const content = processOrVersionToContent(
    canDisplayVersionInterface ? processVersion : null,
    process
  );
  const previousVersion =
    content != null && processVersions != null && versionId != null
      ? formatPreviousProcessVersion(
          getComparedVersion(
            content.url,
            processVersions.results,
            location.query['selected-version-group']
          )
        ) || null
      : null;

  const managerCannotApprove = () => {
    // Some managers can edit the process, but not every attached procedure
    // block them from approving.
    return (
      process.can_edit &&
      !every(
        content.content.filter((i) => i.type === 'processprocedure'),
        'can_edit'
      )
    );
  };

  const scrollIntoStep = (stepId: string) => {
    const stepNode = document.getElementById(stepId);
    const currentStepHash = window.location.hash.replace('#', '');

    if (stepNode != null) {
      if (stepId === currentStepHash) {
        /* on page open */
        stepNode.scrollIntoView({
          behavior: 'smooth',
        });
      } else {
        /* on step link click */
        window.location.href = `#${stepId}`;
      }
    }
  };

  const toggleSidebarDiagram = () => {
    actions.setProcessSideDiagramState(process.id, !closedSideDiagram);
  };

  const toggleDiagramOverlay = () => {
    setShowOverlayDiagram(!showOverlayDiagram);
  };

  const orphans = getMemoizedProcessOrphanSteps(
    content.content,
    process.steps_blocked_at
  );
  const stepsWithoutOrphanChoices = removeOrphanChoices(content.content);

  return (
    <CollapsedStepsState stepsIds={content.content.map((s) => s.id)}>
      {({
        collapsedSteps,
        numCollapsedSteps,
        toggleAllStepsCollapse,
        toggleStepCollapse,
      }) => (
        <VersionDiffer
          content={content}
          location={location}
          previousVersion={previousVersion}
          versionAvailable={canDisplayVersionInterface}
        >
          {(contentWithDiff, showingDiff, toggleDiff) => {
            const actionButtonsProps = {
              contentType: 'proceduregroup' as const,
              document: process,
              canChange: process.can_change,
              refreshData: loadProcess,
              disableApproval: managerCannotApprove(),
              additionalContent: () => (
                <ProcedureHeaderOptionsDropdown
                  advancedModeEnabled={advancedModeEnabled}
                  toggleAdvancedMode={actions.toggleProcedureAdvancedMode}
                  numCollapsedSteps={0}
                  onToggleAllStepsCollapse={() => null}
                  stepsLength={contentWithDiff.content.length}
                  hideAdvancedMode={Boolean(versionId)}
                />
              ),
            };

            return (
              <SPPage
                pageTitle={process.name}
                horizontalPadding={false}
                breadcrumbProps={{
                  docName: getProcessName(content.name),
                }}
                actionArea={
                  canDisplayVersionInterface ? (
                    <GenericDocumentActionButtons
                      pageContext="view-version"
                      isLastVersion={isLastVersion<ProcessVersionSerializedType>(
                        processVersion,
                        processVersions.results
                      )}
                      version={processVersion}
                      {...actionButtonsProps}
                    />
                  ) : (
                    <GenericDocumentActionButtons
                      pageContext="view-published"
                      {...actionButtonsProps}
                    />
                  )
                }
                sidebar={
                  <ProcessPageSidebar
                    acceptance={acceptance}
                    fetchProcess={fetchProcess}
                    process={process}
                    versionId={versionId}
                    versions={processVersions.results}
                    fetchProcessVersions={fetchProcessVersions}
                    version={processVersion}
                    userId={user.id}
                    route={route}
                    router={router}
                  />
                }
                versionbar={
                  <>
                    {canDisplayVersionInterface ? (
                      <VersionInfoBar
                        objectTypeName={gt('process')}
                        version={processVersion}
                        className={'sidebar-visible'}
                        showingDiff={showingDiff}
                        toggleDiff={toggleDiff}
                      />
                    ) : null}
                    {!route?.props.showAccept && acceptance != null ? (
                      <PendingAcceptanceInfoBar
                        document={process}
                        acceptance={acceptance}
                        location={location}
                        openedVersion={processVersion}
                      />
                    ) : null}
                    {versionId == null ? (
                      <SignoffRequiredInfoBar
                        contentType="proceduregroup"
                        objectId={process.id}
                      />
                    ) : null}
                  </>
                }
                alternativeOverlayContent={
                  showOverlayDiagram ? (
                    <ProcedureDiagramMobile
                      toggleDiagram={toggleDiagramOverlay}
                      steps={stepsWithoutOrphanChoices}
                    />
                  ) : null
                }
              >
                <DocumentPresenceMembers
                  contentType="proceduregroup"
                  objectId={process.id}
                  pageContext="view"
                  contexts={['view', 'edit', 'task']}
                  containerClass={cs('col-12', {
                    'col-md-8': !closedSideDiagram,
                  })}
                />

                <ViewProcessMetadata
                  content={contentWithDiff}
                  process={process}
                  policies={process.policies}
                  tags={process.tags}
                  advancedModeEnabled={advancedModeEnabled}
                  warningComponent={
                    <Fragment>
                      {showNoApprovedVersionsAlertBoxIfNotApproved(
                        'proceduregroup',
                        process.approved_at
                      )}
                      <ReviewReminderWarning
                        reviewDate={process.next_review_at}
                        {...{
                          contentType: 'proceduregroup',
                          versionId: process.current_version.id,
                          versionContentType: 'processversion',
                          canReview: process.can_edit,
                          onReview: loadProcess,
                        }}
                      />
                    </Fragment>
                  }
                />

                <StyledStepsListDIV className="js-document-body container pt-0">
                  <div className="row">
                    <div
                      className={cs('col', 'pt-4', 'col-12', {
                        'col-md-8': !closedSideDiagram,
                      })}
                    >
                      <ViewStepsModeTitle
                        title={gt('Procedures List')}
                        numCollapsedSteps={numCollapsedSteps}
                        toggleAllStepsCollapse={toggleAllStepsCollapse}
                      />
                      {contentWithDiff.content.length ? (
                        <ol className="steps mt-4">
                          {contentWithDiff.content.map((step) => (
                            <li key={step.id} id={step.id}>
                              <ProcedureStep
                                index={step.id}
                                step={step}
                                orphan={orphans.has(step.id)}
                                showCopyLink={versionId == null}
                                collapsed={collapsedSteps[step.id]}
                                mediaURL={settings.MEDIA_URL}
                                mediaURLRead={settings.MEDIA_URL_READ}
                                onToggleCollapse={toggleStepCollapse}
                                choicesList={
                                  step.type === 'decision' ? (
                                    <ViewableChoices
                                      choices={step.choices}
                                      steps={content.content}
                                      edit={false}
                                    />
                                  ) : null
                                }
                                next={
                                  <StepFooter
                                    step={step}
                                    steps={content.content}
                                    stepLaneId={content.lanes[step.id]}
                                    laneDiff={
                                      contentWithDiff?.lanesDiff?.[step.id] ??
                                      undefined
                                    }
                                  />
                                }
                              />
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <EmptyProcessList
                          processHTMLURL={process.html_url}
                          canChange={process.can_change}
                        />
                      )}
                      {versionId == null ? (
                        <SignoffForm
                          objectId={process.id}
                          contentType="proceduregroup"
                          versionId={process.current_version.id}
                          versionContentType="processversion"
                          objectName={process.name}
                        />
                      ) : null}
                    </div>
                    {!showOverlayDiagram ? (
                      <ProcedureDiagramSidebar
                        onStepClick={scrollIntoStep}
                        className="d-print-none col-4 mt-4"
                        showingSidebarDiagram={!closedSideDiagram}
                        toggleSidebarDiagram={toggleSidebarDiagram}
                        toggleOverlayDiagram={toggleDiagramOverlay}
                        versionId={process.current_version.id}
                        versionContentType="processversion"
                        steps={stepsWithoutOrphanChoices}
                      />
                    ) : null}
                  </div>
                  <ToggleProcedureDiagramMobileButton
                    showingDiagram={false}
                    toggleDiagram={toggleDiagramOverlay}
                  />
                </StyledStepsListDIV>
              </SPPage>
            );
          }}
        </VersionDiffer>
      )}
    </CollapsedStepsState>
  );
}

export const ProcessPage = withRouter(BaseProcessPage);
